import * as Sentry from '@sentry/react';
import type { SessionTerminalContext } from './sessionTerminal';

const SENTRY_DSN = import.meta.env.VITE_SENTRY_DSN;
const UUID_PATTERN =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
const SERVER_STATUS_PATTERN = /^[A-Z0-9_.:-]{1,64}$/i;
const SESSION_CREDENTIAL_PATTERN = /^sess_[A-Za-z0-9]{20}$/;
const QR_CREDENTIAL_PATTERN = /^(?:ST|SP|VW)-[A-Z0-9]{12}$/;
const SAFE_REASONS = new Set([
  'completed',
  'expired',
  'unauthorized',
  'not_found',
  'invalid',
  'unknown',
  'manual_end',
]);
const SAFE_SOURCES = new Set([
  'initial_entry',
  'initial_fetch',
  'poll',
  'countdown',
  'extend',
  'manual_end',
]);
const FALLBACK_EVENT_ID = '00000000-0000-4000-8000-000000000000';

export interface SessionEndedTelemetryInput {
  eventId: string;
  sessionId: number | null;
  occurredAt: string;
  httpStatus?: number | null;
  serverStatus?: string | null;
}

interface TerminalTelemetryFields extends Record<string, unknown> {
  event_id: string;
  reason: string;
  source: string;
  http_status: number | null;
  server_status: string | null;
  session_id: number | null;
  occurred_at: string;
}

function createSafeEventId(value: unknown): string {
  if (typeof value === 'string' && UUID_PATTERN.test(value)) return value;

  try {
    const generated = globalThis.crypto?.randomUUID();
    return generated && UUID_PATTERN.test(generated) ? generated : FALLBACK_EVENT_ID;
  } catch {
    return FALLBACK_EVENT_ID;
  }
}

function createSafeOccurredAt(value: unknown): string {
  if (typeof value === 'string') {
    const timestamp = Date.parse(value);
    if (Number.isFinite(timestamp) && new Date(timestamp).toISOString() === value) {
      return value;
    }
  }

  return new Date().toISOString();
}

function isSafeServerStatus(value: unknown): value is string {
  return (
    typeof value === 'string' &&
    SERVER_STATUS_PATTERN.test(value) &&
    !SESSION_CREDENTIAL_PATTERN.test(value) &&
    !QR_CREDENTIAL_PATTERN.test(value)
  );
}

function sanitizeTerminalTelemetryFields(
  fields: TerminalTelemetryFields,
): TerminalTelemetryFields {
  return {
    event_id: createSafeEventId(fields.event_id),
    reason:
      typeof fields.reason === 'string' && SAFE_REASONS.has(fields.reason)
        ? fields.reason
        : 'unknown',
    source:
      typeof fields.source === 'string' && SAFE_SOURCES.has(fields.source)
        ? fields.source
        : 'initial_entry',
    http_status:
      fields.http_status === null ||
      (Number.isInteger(fields.http_status) &&
        fields.http_status >= 100 &&
        fields.http_status <= 599)
        ? fields.http_status
        : null,
    server_status: isSafeServerStatus(fields.server_status)
      ? fields.server_status
      : null,
    session_id:
      fields.session_id === null ||
      (Number.isInteger(fields.session_id) && fields.session_id >= 0)
        ? fields.session_id
        : null,
    occurred_at: createSafeOccurredAt(fields.occurred_at),
  };
}

function captureTerminalEvent(
  eventName: 'table_session_terminal' | 'session_ended',
  level: 'warning' | 'info',
  fields: TerminalTelemetryFields,
): void {
  const safeFields = sanitizeTerminalTelemetryFields(fields);

  if (import.meta.env.DEV || !SENTRY_DSN) {
    try {
      console.warn('[Session telemetry fallback]', {
        event: eventName,
        ...safeFields,
      });
    } catch {
      // Console availability must not affect the session lifecycle.
    }
  }

  if (!SENTRY_DSN) return;

  try {
    Sentry.withScope((scope) => {
      scope.setTag('event', eventName);
      scope.setTag('reason', safeFields.reason);
      scope.setExtras(safeFields);
      Sentry.captureMessage(eventName, { level });
    });
  } catch {
    // Telemetry must never delay or interrupt session cleanup and navigation.
  }
}

export function recordTableSessionTerminal(context: SessionTerminalContext): void {
  captureTerminalEvent('table_session_terminal', 'warning', {
    event_id: context.eventId,
    reason: context.reason,
    source: context.source,
    http_status: context.httpStatus,
    server_status: context.serverStatus,
    session_id: context.sessionId,
    occurred_at: context.occurredAt,
  });
}

export function recordSessionEnded(input: SessionEndedTelemetryInput): void {
  captureTerminalEvent('session_ended', 'info', {
    event_id: input.eventId,
    reason: 'manual_end',
    source: 'manual_end',
    http_status: input.httpStatus ?? null,
    server_status: input.serverStatus ?? null,
    session_id: input.sessionId,
    occurred_at: input.occurredAt,
  });
}
